import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, ButtonGroup, Form, Badge, Tab, Tabs, Table, Modal, Row, Col, Alert, Spinner, InputGroup, Card } from '@/components/ui';
import UsersTable from './components/UsersTable';
import UserModal from './components/UserModal';
import UserHistoryModal from '../../components/UserHistoryModal';
import FiltersCard from '../../components/FiltersCard';
import ServerPagination from '../../components/ServerPagination';
import RolesTable from '../roles/components/RolesTable';
import RoleModal from '../roles/components/RoleModal';
import ClassificationModal from '../classifications/components/ClassificationModal';
import type { User } from '../../types/User';
import type { Role } from '../../types/Role';
import type { Permission } from '../../types/User';
import type { Classification } from '../../types/Employee';
import type { ServiceArea, ForemanArea } from '../../services/areasService';
import { usersService } from '../../services/usersService';
import { rolesService } from '../../services/rolesService';
import { classificationService } from '../../services/classificationService';
import { areasService } from '../../services/areasService';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type Season = 'summer' | 'winter' | null;
type StatusFilter = 'all' | 'active' | 'inactive';
type PortalFilter = 'all' | 'portal' | 'without-portal';
type UsersTab = 'employees' | 'classifications' | 'roles';

const USERS_TAB_TITLES: Record<UsersTab, string> = {
  employees: 'Employees',
  classifications: 'Classifications & Rates',
  roles: 'Roles & Permissions',
};

const UsersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Load season from localStorage or default to null (none selected)
  const [season, setSeason] = useState<Season>(() => {
    const savedSeason = localStorage.getItem('employeeSeasonPreference');
    return (savedSeason === 'summer' || savedSeason === 'winter') ? savedSeason : null;
  });
  
  // State for season change confirmation (for master toggle)
  const [showSeasonConfirm, setShowSeasonConfirm] = useState(false);
  const [pendingSeason, setPendingSeason] = useState<'summer' | 'winter' | null>(null);
  const [bulkUpdateCount, setBulkUpdateCount] = useState(0);

  // Save season to localStorage when it changes (view preference only)
  const handleSeasonChange = (newSeason: 'summer' | 'winter') => {
    // Show confirmation if season is actually changing (and not null)
    if (newSeason !== season && newSeason !== null) {
      // Count all users that would be affected across ALL pages (all users matching current filters)
      // This uses the total count from pagination, which represents all filtered users across all pages
      const affectedCount = pagination.totalCount || users.length;
      setBulkUpdateCount(affectedCount);
      setPendingSeason(newSeason);
      setShowSeasonConfirm(true);
    }
  };

  const confirmSeasonChange = async () => {
    if (pendingSeason) {
      // Bulk update all filtered employees
      try {
        // Build filters object from current filter state
        const filters: any = {};
        if (statusFilter === 'active') {
          filters.status = 'ACTIVE';
        } else if (statusFilter === 'inactive') {
          filters.status = 'DISABLED';
        }
        if (portalFilter === 'portal') {
          filters.hasPortalAccess = true;
        } else if (portalFilter === 'without-portal') {
          filters.hasPortalAccess = false;
        }
        if (employeeServiceAreaId) {
          filters.serviceAreaId = employeeServiceAreaId;
        }
        if (employeeForemanAreaId) {
          filters.foremanAreaId = employeeForemanAreaId;
        }
        if (employeeRoleId) {
          filters.roleId = employeeRoleId;
        }
        if (searchTerm) {
          filters.search = searchTerm;
        }

        // Pass null for userIds to use filters instead
        const result = await usersService.bulkUpdateSeason(null, pendingSeason, filters);
        // Small delay to ensure cache is cleared
        await new Promise(resolve => setTimeout(resolve, 100));
        // Refresh users to get updated data - reset to page 1 to see all updated users
        await fetchUsers(1, true);
        setSeason(pendingSeason);
        localStorage.setItem('employeeSeasonPreference', pendingSeason);
      } catch (error) {
        console.error('Error bulk updating seasons:', error);
        // Still update the view preference even if bulk update fails
      setSeason(pendingSeason);
      localStorage.setItem('employeeSeasonPreference', pendingSeason);
      }
    }
    setShowSeasonConfirm(false);
    setPendingSeason(null);
    setBulkUpdateCount(0);
  };

  const cancelSeasonChange = () => {
    setShowSeasonConfirm(false);
    setPendingSeason(null);
    setBulkUpdateCount(0);
  };

  // Handle individual user season change
  const handleUserSeasonChange = async (user: User, newSeason: 'summer' | 'winter') => {
    try {
      await usersService.updateUserSeason(user.id, newSeason);
      // Refresh users to get updated data
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user season:', error);
    }
  };
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0
  });
  // Employees tab server-side sort
  const [userSortBy, setUserSortBy] = useState<string | null>(null);
  const [userSortDir, setUserSortDir] = useState<'asc' | 'desc'>('asc');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<User | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [portalFilter, setPortalFilter] = useState<PortalFilter>('all');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // Employee filter state - pending values (not applied until Apply is clicked)
  const [pendingStatusFilter, setPendingStatusFilter] = useState<StatusFilter>('active');
  const [pendingPortalFilter, setPendingPortalFilter] = useState<PortalFilter>('all');
  const [pendingServiceAreaId, setPendingServiceAreaId] = useState<number | ''>('');
  const [pendingForemanAreaId, setPendingForemanAreaId] = useState<number | ''>('');
  const [pendingRoleId, setPendingRoleId] = useState<number | ''>('');
  
  // Applied filter state (used for actual filtering)
  const [employeeServiceAreaId, setEmployeeServiceAreaId] = useState<number | ''>('');
  const [employeeForemanAreaId, setEmployeeForemanAreaId] = useState<number | ''>('');
  const [employeeRoleId, setEmployeeRoleId] = useState<number | ''>('');
  const [foremanAreas, setForemanAreas] = useState<any[]>([]);
  const [allRoles, setAllRoles] = useState<Role[]>([]);
  
  // Role management state
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [roleSearchInput, setRoleSearchInput] = useState('');
  const [isRoleSearchVisible, setIsRoleSearchVisible] = useState(false);
  const [roleSortBy, setRoleSortBy] = useState<string | null>(null);
  const [roleSortDir, setRoleSortDir] = useState<'asc' | 'desc'>('asc');
  const [rolePagination, setRolePagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0
  });
  
  // Classifications state
  const [showClassificationModal, setShowClassificationModal] = useState(false);
  const [selectedClassification, setSelectedClassification] = useState<Classification | null>(null);
  const [classifications, setClassifications] = useState<Classification[]>([]);
  const [serviceAreas, setServiceAreas] = useState<ServiceArea[]>([]);
  const [selectedServiceAreaId, setSelectedServiceAreaId] = useState<number | ''>('');
  const [classificationSearchTerm, setClassificationSearchTerm] = useState('');
  const [classificationSearchInput, setClassificationSearchInput] = useState('');
  const [isClassificationSearchVisible, setIsClassificationSearchVisible] = useState(false);
  const [classificationPagination, setClassificationPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [classificationLoading, setClassificationLoading] = useState(false);
  const [classificationError, setClassificationError] = useState<string | null>(null);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { hasPermission } = usePermissions();
  const { currentUser } = useAuth();
  
  // Check if user is admin (has user:edit or user:create permissions, or role name is Admin)
  const isAdmin = hasPermission('user:edit') || hasPermission('user:create') || currentUser?.role?.name === 'Admin';
  
  // Classification permissions
  const canCreateClassification = hasPermission('classification:create') || isAdmin;
  const canEditClassification = hasPermission('classification:edit') || isAdmin;

  const canSeeEmployees = hasPermission('user:create') || hasPermission('employee:view');
  const canSeeClassifications = hasPermission('classification:view') || isAdmin;
  const canSeeRoles = hasPermission('role:view');
  const defaultTab: UsersTab = canSeeEmployees
    ? 'employees'
    : canSeeClassifications
      ? 'classifications'
      : 'roles';
  const requestedTab = searchParams.get('tab');
  const activeTab: UsersTab =
    requestedTab === 'employees' && canSeeEmployees
      ? 'employees'
      : requestedTab === 'classifications' && canSeeClassifications
        ? 'classifications'
        : requestedTab === 'roles' && canSeeRoles
          ? 'roles'
          : defaultTab;

  const handleTabSelect = (key: string | null) => {
    const next: UsersTab =
      key === 'employees' || key === 'classifications' || key === 'roles' ? key : defaultTab;
    setSearchParams({ tab: next }, { replace: true });
  };

  const fetchUsers = async (page?: number, resetPage: boolean = false) => {
    try {
      setLoading(true);
      
      const currentPage = resetPage ? 1 : (page || pagination.page);
      
      const filters: any = {
        search: searchTerm || undefined,
        ...(userSortBy ? { sortBy: userSortBy, sortOrder: userSortDir } : {})
      };

      // Apply status filter
      if (statusFilter === 'active') {
        filters.status = 'ACTIVE';
      } else if (statusFilter === 'inactive') {
        filters.status = 'DISABLED';
      }
      // 'all' doesn't need any filter

      // Apply portal access filter
      if (portalFilter === 'portal') {
        filters.hasPortalAccess = true;
      } else if (portalFilter === 'without-portal') {
        filters.hasPortalAccess = false;
      }
      // 'all' doesn't need any filter

      // Apply service area filter
      if (employeeServiceAreaId) {
        filters.serviceAreaId = employeeServiceAreaId;
      }

      // Apply foreman area filter
      if (employeeForemanAreaId) {
        filters.foremanAreaId = employeeForemanAreaId;
      }

      // Apply role filter
      if (employeeRoleId) {
        filters.roleId = employeeRoleId;
      }
      
      // Skip Redis cache to prevent crashes
      const usersData = await usersService.getUsers(currentPage, pagination.limit, filters, true); // skipCache: true
      setUsers(usersData.users);
      setPagination({
        page: usersData.pagination.page,
        limit: usersData.pagination.limit,
        totalCount: usersData.pagination.total,
        totalPages: usersData.pagination.totalPages
      });
      
      // Update master season based on ALL filtered employees' seasons across ALL pages
      // We need to check all filtered users, not just the current page
      // Fetch a larger sample or all users to determine if seasons are mixed
      if (usersData.pagination.total > 0) {
        // Fetch all filtered users (or a large sample) to check their seasons
        const allUsersData = await usersService.getUsers(1, Math.min(usersData.pagination.total, 1000), filters);
        const allSeasons = allUsersData.users.map((user: User) => (user as any).activeSeason || 'summer');
        const uniqueSeasons = Array.from(new Set(allSeasons));
        
        if (uniqueSeasons.length === 1) {
          // All employees across all pages have the same season - set master season to match
          // This allows shift length to display correctly and shows the season in the global filter
          const commonSeason = uniqueSeasons[0] as 'summer' | 'winter';
          setSeason(commonSeason);
          localStorage.setItem('employeeSeasonPreference', commonSeason);
        } else if (uniqueSeasons.length > 1) {
          // Mixed seasons across all pages - ALWAYS reset to null (none selected) so user can choose either
          setSeason(null);
          localStorage.removeItem('employeeSeasonPreference');
        }
      } else {
        // No employees found - reset to null (none selected)
        setSeason(null);
        localStorage.removeItem('employeeSeasonPreference');
      }
      
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async (page: number = 1, search: string = roleSearchTerm, limit: number = rolePagination.limit) => {
    if (!hasPermission('role:view')) return;

    try {
      const rolesData = await rolesService.getRoles(page, limit, search, roleSortBy || undefined, roleSortDir);
      setRoles(rolesData.roles);
      setRolePagination({
        page: rolesData.pagination.page,
        limit: rolesData.pagination.limit,
        totalCount: rolesData.pagination.total,
        totalPages: rolesData.pagination.totalPages
      });
      const permissionsData = await rolesService.getPermissions();
      setPermissions(permissionsData);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  // Fetch filter options for employee tab
  useEffect(() => {
    if (activeTab === 'employees') {
      const fetchFilterOptions = async () => {
        try {
          const [serviceAreasData, foremanAreasData, rolesData] = await Promise.all([
            areasService.getServiceAreas(),
            areasService.getForemanAreas(),
            rolesService.getRoles(1, 1000) // Get all roles
          ]);
          setServiceAreas(serviceAreasData);
          setForemanAreas(foremanAreasData);
          setAllRoles(rolesData.roles);
        } catch (err) {
          console.error('Error fetching filter options:', err);
        }
      };
      fetchFilterOptions();
    }
  }, [activeTab]);

  // Filter roles based on selected service area
  const filteredRoles = useMemo(() => {
    if (!pendingServiceAreaId || !allRoles.length) {
      return allRoles;
    }
    
    // Filter roles that have at least one classification in the selected service area
    return allRoles.filter(role => {
      if (!role.roleClassifications || role.roleClassifications.length === 0) {
        // If role has no classifications, include it (some roles might not have classifications)
        return true;
      }
      // Check if any classification belongs to the selected service area
      return role.roleClassifications.some(rc => 
        rc.classification?.serviceArea?.id === pendingServiceAreaId
      );
    });
  }, [allRoles, pendingServiceAreaId]);

  // Clear role selection if it's no longer available when service area changes
  useEffect(() => {
    if (pendingServiceAreaId && pendingRoleId) {
      const isRoleAvailable = filteredRoles.some(role => role.id === pendingRoleId);
      if (!isRoleAvailable) {
        setPendingRoleId('');
      }
    }
  }, [pendingServiceAreaId, filteredRoles, pendingRoleId]);

  // Sync pending filters with applied filters when tab changes
  useEffect(() => {
    if (activeTab === 'employees') {
      setPendingStatusFilter(statusFilter);
      setPendingPortalFilter(portalFilter);
      setPendingServiceAreaId(employeeServiceAreaId);
      setPendingForemanAreaId(employeeForemanAreaId);
      setPendingRoleId(employeeRoleId);
    }
  }, [activeTab]);

  // Fetch users when filters or pagination change
  useEffect(() => {
    if (activeTab === 'employees') {
      // Use fetchUsers to ensure season is updated based on filtered employees
      fetchUsers();
    }
  }, [statusFilter, portalFilter, searchTerm, employeeServiceAreaId, employeeForemanAreaId, employeeRoleId, pagination.page, pagination.limit, activeTab, userSortBy, userSortDir]);

  const handleUserSort = (key: string, dir: 'asc' | 'desc') => {
    setUserSortBy(key);
    setUserSortDir(dir);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Fetch roles when tab changes or pagination changes
  useEffect(() => {
    if (activeTab === 'roles') {
      fetchRoles(rolePagination.page, roleSearchTerm, rolePagination.limit);
    }
  }, [activeTab, rolePagination.page, rolePagination.limit, roleSearchTerm, roleSortBy, roleSortDir]);

  const handleRoleSort = (key: string, dir: 'asc' | 'desc') => {
    setRoleSortBy(key);
    setRoleSortDir(dir);
    setRolePagination(prev => ({ ...prev, page: 1 }));
  };

  // Initial fetch for roles
  useEffect(() => {
    if (hasPermission('role:view')) {
      fetchRoles(1);
    }
  }, []);

  // Fetch service areas for classifications
  useEffect(() => {
    if (activeTab === 'classifications') {
      fetchServiceAreas();
    }
  }, [activeTab]);

  // Fetch classifications when tab is active or filters change
  useEffect(() => {
    if (activeTab === 'classifications') {
      fetchClassifications();
    }
  }, [activeTab, selectedServiceAreaId, classificationSearchTerm, classificationPagination.page, classificationPagination.limit]);

  const handleAddUser = () => {
    setSelectedUser(null);
    setShowUserModal(true);
  };

  const handleRowClick = (user: User) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const handleSaveUser = async (userData: Partial<User>): Promise<User> => {
    try {
      let savedUser: User;
      if (selectedUser) {
        savedUser = await usersService.updateUser(selectedUser.id, userData);
      } else {
        savedUser = await usersService.createUser(userData);
      }
      await fetchUsers(pagination.page);
      setShowUserModal(false);
      setSelectedUser(null);
      return savedUser;
    } catch (err: any) {
      console.error('Error saving user:', err);
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.error || 
                          'Failed to save user. Please try again.';
      alert(errorMessage);
      throw err; // Re-throw so modal can handle it
    }
  };

  const handleViewHistory = (user: User) => {
    setSelectedUser(user);
    setShowHistoryModal(true);
  };

  const handleActivate = async (user: User) => {
    try {
      await usersService.updateUser(user.id, { status: 'ACTIVE' });
      await fetchUsers(pagination.page);
    } catch (err: any) {
      console.error('Error activating user:', err);
      alert('Failed to activate user. Please try again.');
    }
  };

  const handleDeactivateClick = (user: User) => {
    setUserToDeactivate(user);
  };

  const handleDeactivateConfirm = async () => {
    if (!userToDeactivate) return;
    try {
      await usersService.updateUser(userToDeactivate.id, { status: 'DISABLED' });
      await fetchUsers(pagination.page);
      setUserToDeactivate(null);
    } catch (err: any) {
      console.error('Error deactivating user:', err);
      alert('Failed to deactivate user. Please try again.');
    }
  };

  const getDeactivateMessage = (user: User) => {
    const parts: string[] = [];
    if (user.hasPortalAccess) parts.push('they will no longer have WRAP access');
    if (user.status === 'ACTIVE') parts.push('they will no longer be available in TrueContext dropdowns');
    if (parts.length === 0) parts.push('they will no longer be active in the system');
    const consequence = parts.join(' or ');
    return `Are you sure you'd like to deactivate this employee? The employee history will remain in the system, but ${consequence}.`;
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }));
  };

  // Apply filters handler
  const handleApplyFilters = () => {
    setStatusFilter(pendingStatusFilter);
    setPortalFilter(pendingPortalFilter);
    setEmployeeServiceAreaId(pendingServiceAreaId);
    setEmployeeForemanAreaId(pendingForemanAreaId);
    setEmployeeRoleId(pendingRoleId);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Reset filters handler
  const handleResetFilters = () => {
    setPendingStatusFilter('all');
    setPendingPortalFilter('all');
    setPendingServiceAreaId('');
    setPendingForemanAreaId('');
    setPendingRoleId('');
    setStatusFilter('all');
    setPortalFilter('all');
    setEmployeeServiceAreaId('');
    setEmployeeForemanAreaId('');
    setEmployeeRoleId('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Check if there are active filters
  const hasActiveFilters = () => {
    return statusFilter !== 'all' || 
           portalFilter !== 'all' || 
           employeeServiceAreaId !== '' || 
           employeeForemanAreaId !== '' || 
           employeeRoleId !== '';
  };

  // Check if there are unapplied filter changes
  const hasUnappliedChanges = () => {
    return pendingStatusFilter !== statusFilter ||
           pendingPortalFilter !== portalFilter ||
           pendingServiceAreaId !== employeeServiceAreaId ||
           pendingForemanAreaId !== employeeForemanAreaId ||
           pendingRoleId !== employeeRoleId;
  };

  // Role handlers
  const handleAddRole = () => {
    setSelectedRole(null);
    setShowRoleModal(true);
  };

  const handleEditRole = (role: Role) => {
    setSelectedRole(role);
    setShowRoleModal(true);
  };

  const handleSaveRole = async (roleData: Partial<Role>) => {
    try {
      if (selectedRole) {
        await rolesService.updateRole(selectedRole.id, roleData);
      } else {
        await rolesService.createRole(roleData);
      }
      await fetchRoles(rolePagination.page);
      setShowRoleModal(false);
    } catch (err) {
      console.error('Error saving role:', err);
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        await rolesService.deleteRole(roleId);
        await fetchRoles(rolePagination.page);
      } catch (err) {
        console.error('Error deleting role:', err);
        const axiosErr = err as { response?: { data?: { error?: string; details?: string } } };
        window.alert(
          axiosErr.response?.data?.error ||
            axiosErr.response?.data?.details ||
            'Failed to delete role'
        );
      }
    }
  };

  const handleRolePageChange = (page: number) => {
    setRolePagination(prev => ({ ...prev, page }));
  };

  const handleRoleSearch = () => {
    setRoleSearchTerm(roleSearchInput.trim());
    setRolePagination(prev => ({ ...prev, page: 1 }));
  };

  const toggleRoleSearch = () => {
    setIsRoleSearchVisible(!isRoleSearchVisible);
    if (isRoleSearchVisible) {
      setRoleSearchInput('');
      setRoleSearchTerm('');
      setRolePagination(prev => ({ ...prev, page: 1 }));
    }
  };

  const clearRoleSearch = () => {
    setRoleSearchInput('');
    setRoleSearchTerm('');
    setRolePagination(prev => ({ ...prev, page: 1 }));
  };

  // Search handlers
  const toggleSearch = () => {
    setIsSearchVisible(!isSearchVisible);
    if (isSearchVisible) {
      setSearchInput('');
      setSearchTerm('');
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput.trim());
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  // Classification handlers
  const fetchServiceAreas = async () => {
    try {
      const areas = await areasService.getServiceAreas();
      setServiceAreas(areas);
    } catch (err) {
      console.error('Error fetching service areas:', err);
    }
  };

  const fetchClassifications = async () => {
    try {
      setClassificationLoading(true);
      setClassificationError(null);
      
      const filters: any = {
        search: classificationSearchTerm || undefined,
        serviceAreaId: selectedServiceAreaId || undefined
      };
      
      const response = await classificationService.getClassifications(classificationPagination.page, classificationPagination.limit, filters);
      setClassifications(response.classifications);
      setClassificationPagination(response.pagination);
    } catch (err) {
      console.error('Error fetching classifications:', err);
      setClassificationError('Failed to fetch classifications');
    } finally {
      setClassificationLoading(false);
    }
  };

  const handleAddClassification = () => {
    setSelectedClassification(null);
    setShowClassificationModal(true);
  };

  const handleEditClassification = (classification: Classification) => {
    setSelectedClassification(classification);
    setShowClassificationModal(true);
  };

  const handleDeleteClassification = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this classification?')) {
      return;
    }

    try {
      await classificationService.deleteClassification(id);
      await fetchClassifications();
    } catch (err: any) {
      console.error('Error deleting classification:', err);
      alert('Failed to delete classification. Please try again.');
    }
  };

  const handleSaveClassification = async (classificationData: Partial<Classification>) => {
    try {
      if (selectedClassification) {
        await classificationService.updateClassification(selectedClassification.id, classificationData);
      } else {
        await classificationService.createClassification(classificationData as { code: string; rate: number; serviceAreaId: number });
      }
      await fetchClassifications();
      setShowClassificationModal(false);
      setSelectedClassification(null);
    } catch (err: any) {
      console.error('Error saving classification:', err);
      const errorMessage = err?.response?.data?.message || 
                          err?.response?.data?.error || 
                          'Failed to save classification. Please try again.';
      alert(errorMessage);
    }
  };

  const handleClassificationPageChange = (page: number) => {
    setClassificationPagination(prev => ({ ...prev, page }));
  };

  const handleClassificationPageSizeChange = (pageSize: number) => {
    setClassificationPagination(prev => ({ ...prev, limit: pageSize, page: 1 }));
  };

  const toggleClassificationSearch = () => {
    setIsClassificationSearchVisible(!isClassificationSearchVisible);
    if (isClassificationSearchVisible) {
      setClassificationSearchInput('');
      setClassificationSearchTerm('');
    }
  };

  const handleClassificationSearch = () => {
    setClassificationSearchTerm(classificationSearchInput.trim());
    setClassificationPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClassificationSearchInputChange = (value: string) => {
    setClassificationSearchInput(value);
  };

  const handleClassificationSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleClassificationSearch();
    }
  };

  const clearClassificationSearch = () => {
    setClassificationSearchInput('');
    setClassificationSearchTerm('');
  };


  return (
    <div className="tw-flex tw-w-full tw-flex-1 tw-min-h-0 tw-flex-col tw-p-4">
      {/* Header */}
      <div className="tw-mb-3 tw-flex tw-shrink-0 tw-items-center tw-justify-between">
        <div>
          <h1>{USERS_TAB_TITLES[activeTab]}</h1>
        </div>
        <div className="tw-flex tw-gap-2">
          {activeTab === 'employees' && (
            <>
              <Button
                variant={isSearchVisible ? 'primary' : 'outline-primary'}
                active={!!searchTerm}
                onClick={toggleSearch}
                title={isSearchVisible ? "Close search" : "Search employees"}
              >
                <i className={`bi ${isSearchVisible ? 'bi-x-lg' : 'bi-search'} tw-mr-2`}></i>
                <span>Search {searchTerm ? `(${searchTerm.length > 20 ? searchTerm.substring(0, 20) + '...' : searchTerm})` : ''}</span>
              </Button>
              {(hasPermission('user:create') || hasPermission('employee:create')) && (
                <Button variant="primary" onClick={handleAddUser}>
                  <i className="bi bi-plus-lg tw-mr-2"></i>
                  Add Employee
                </Button>
              )}
            </>
          )}
          {activeTab === 'roles' && hasPermission('role:create') && (
            <>
              <Button
                variant={isRoleSearchVisible ? 'primary' : 'outline-primary'}
                active={!!roleSearchTerm}
                onClick={toggleRoleSearch}
                title={isRoleSearchVisible ? 'Close search' : 'Search roles'}
              >
                <i className={`bi ${isRoleSearchVisible ? 'bi-x-lg' : 'bi-search'} tw-mr-2`}></i>
                <span>Search {roleSearchTerm ? `(${roleSearchTerm.length > 20 ? roleSearchTerm.substring(0, 20) + '...' : roleSearchTerm})` : ''}</span>
              </Button>
              <Button variant="primary" onClick={handleAddRole}>
                <i className="bi bi-plus-lg tw-mr-2"></i>
                Add Role
              </Button>
            </>
          )}
          {activeTab === 'classifications' && (
            <>
              <Button
                variant={isClassificationSearchVisible ? 'primary' : 'outline-primary'}
                active={!!classificationSearchTerm}
                onClick={toggleClassificationSearch}
                title={isClassificationSearchVisible ? "Close search" : "Search classifications"}
              >
                <i className={`bi ${isClassificationSearchVisible ? 'bi-x-lg' : 'bi-search'} tw-mr-2`}></i>
                <span>Search {classificationSearchTerm ? `(${classificationSearchTerm.length > 20 ? classificationSearchTerm.substring(0, 20) + '...' : classificationSearchTerm})` : ''}</span>
              </Button>
              {canCreateClassification && (
                <Button variant="primary" onClick={handleAddClassification}>
                  <i className="bi bi-plus-lg tw-mr-2"></i>
                  Add Classification
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
      <Tabs
        activeKey={activeTab}
        onSelect={handleTabSelect}
        className="tw-mb-3"
      >
        {canSeeEmployees && (
        <Tab eventKey="employees" title="Employees">
          {/* Filters Card */}
          <FiltersCard
            title="Employee Filters"
            subtitle="Filter employees by status, portal access, service area, foreman area, and role"
            showApplyButton={true}
            onApply={handleApplyFilters}
            applyDisabled={!hasUnappliedChanges()}
            showClearButton={true}
            onClear={handleResetFilters}
            hasActiveFilters={hasActiveFilters()}
          >
            {/* Filters Row */}
            <Row className="tw-items-end tw-gap-y-3">
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-font-semibold tw-text-content-secondary">
                    <i className="bi bi-toggle-on tw-text-primary" aria-hidden="true"></i>
                    Status
                  </Form.Label>
                  <Form.Select
                    value={pendingStatusFilter}
                    onChange={(e) => setPendingStatusFilter(e.target.value as StatusFilter)}
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-font-semibold tw-text-content-secondary">
                    <i className="bi bi-geo-alt-fill tw-text-primary" aria-hidden="true"></i>
                    Service Area
                  </Form.Label>
                  <Form.Select
                    value={pendingServiceAreaId}
                    onChange={(e) => {
                      const value = e.target.value ? parseInt(e.target.value) : '';
                      setPendingServiceAreaId(value);
                      // Clear foreman area if service area changes
                      if (value !== pendingServiceAreaId) {
                        setPendingForemanAreaId('');
                      }
                    }}
                  >
                    <option value="">All Service Areas</option>
                    {serviceAreas.map(sa => (
                      <option key={sa.id} value={sa.id}>
                        {sa.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-font-semibold tw-text-content-secondary">
                    <i className="bi bi-pin-map-fill tw-text-primary" aria-hidden="true"></i>
                    Foreman Area
                  </Form.Label>
                  <Form.Select
                    value={pendingForemanAreaId}
                    onChange={(e) => setPendingForemanAreaId(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    <option value="">All Foreman Areas</option>
                    {foremanAreas
                      .filter(fa => !pendingServiceAreaId || fa.serviceAreaId === pendingServiceAreaId)
                      .map(fa => (
                        <option key={fa.id} value={fa.id}>
                          {fa.name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-font-semibold tw-text-content-secondary">
                    <i className="bi bi-person-badge tw-text-primary" aria-hidden="true"></i>
                    Role
                  </Form.Label>
                  <Form.Select
                    value={pendingRoleId}
                    onChange={(e) => setPendingRoleId(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    <option value="">All Roles</option>
                    {filteredRoles.map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label className="tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-font-semibold tw-text-content-secondary">
                    <i className="bi bi-person-check tw-text-primary" aria-hidden="true"></i>
                    Portal Access
                  </Form.Label>
                  <Form.Select
                    value={pendingPortalFilter}
                    onChange={(e) => setPendingPortalFilter(e.target.value as PortalFilter)}
                  >
                    <option value="all">All</option>
                    <option value="portal">Portal Access</option>
                    <option value="without-portal">No Portal Access</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            {/* Season Row */}
            <Row className="tw-items-center tw-mt-3">
              <Col md={12}>
                <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-3">
                  <Form.Label className="tw-mb-0 tw-flex tw-items-center tw-gap-1.5 tw-font-semibold tw-text-content-secondary">
                    <i className="bi bi-calendar3 tw-text-primary" aria-hidden="true"></i>
                    Season
                  </Form.Label>
                  <ButtonGroup>
                    <Button
                      size="sm"
                      variant={season === 'summer' ? 'warning' : 'outline-warning'}
                      onClick={() => handleSeasonChange('summer')}
                      style={{
                        minWidth: '100px',
                        fontWeight: season === 'summer' ? '600' : '400'
                      }}
                    >
                      <i className="bi bi-sun tw-mr-1"></i>
                      Summer
                    </Button>
                    <Button
                      size="sm"
                      variant={season === 'winter' ? 'primary' : 'outline-primary'}
                      onClick={() => handleSeasonChange('winter')}
                      style={{
                        minWidth: '100px',
                        fontWeight: season === 'winter' ? '600' : '400'
                      }}
                    >
                      <i className="bi bi-snow tw-mr-1"></i>
                      Winter
                    </Button>
                  </ButtonGroup>
                  {season && (
                    <Badge
                      bg={season === 'summer' ? 'warning' : 'primary'}
                      text="white"
                      className="tw-px-3 tw-py-2"
                      style={{ fontSize: '0.9em' }}
                    >
                      <i className={`bi ${season === 'summer' ? 'bi-sun' : 'bi-snow'} tw-mr-1`}></i>
                      {season === 'summer' ? 'Summer' : 'Winter'} Shift Length
                    </Badge>
                  )}
                  </div>
                </Col>
              </Row>
          </FiltersCard>

          {/* Search field appears below filters when visible */}
          {isSearchVisible && (
            <Row className="tw-mb-3">
              <Col xs={12}>
                <Card className="tw-shadow-sm tw-border-0">
                  <Card.Body>
                    <InputGroup>
                      <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                        <i className="bi bi-search"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        className="tw-border-line-focus"
                        placeholder="Search by first name, last name, full name, email, or employee number..."
                        value={searchInput}
                        onChange={(e) => handleSearchInputChange(e.target.value)}
                        onKeyDown={handleSearchKeyPress}
                        autoFocus
                        size="lg"
                        style={{ fontSize: '1rem' }}
                      />
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={handleSearch}
                        title="Search"
                      >
                        <i className="bi bi-search tw-mr-2"></i>
                        Search
                      </Button>
                      {(searchInput || searchTerm) && (
                        <Button
                          variant="outline-secondary"
                          size="lg"
                          onClick={clearSearch}
                          title="Clear search"
                        >
                          <i className="bi bi-x-lg"></i>
                        </Button>
                      )}
                    </InputGroup>
                    {searchTerm && (
                      <div className="tw-mt-2">
                        <Badge bg="info" text="white">
                          <i className="bi bi-search tw-mr-1"></i>
                          Searching for: "{searchTerm}"
                        </Badge>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {error && (
            <Alert variant="danger" className="tw-shadow-sm tw-border-0">
              <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
              {error}
            </Alert>
          )}

          {loading ? (
            <div className="tw-text-center tw-py-5">
              <Spinner className="tw-text-primary" style={{ width: '3rem', height: '3rem' }}>Loading...</Spinner>
              <p className="tw-text-content-muted tw-mt-3">Loading employees...</p>
            </div>
          ) : (
            <>
              <Card className="tw-shadow-sm tw-border-0" style={{ backgroundColor: '#ffffff' }}>
                <Card.Body className="tw-p-0">
                  <UsersTable
                    users={users}
                    onRowClick={handleRowClick}
                    onViewHistory={handleViewHistory}
                    onActivate={handleActivate}
                    onDeactivate={handleDeactivateClick}
                    onSeasonChange={handleUserSeasonChange}
                    season={season}
                    sortBy={userSortBy}
                    sortDir={userSortDir}
                    onSort={handleUserSort}
                  />
                </Card.Body>
              </Card>

              <div className="tw-mt-3">
                <ServerPagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.limit}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  loading={loading}
                />
              </div>

              {/* Deactivate confirmation modal */}
              <Modal show={!!userToDeactivate} onHide={() => setUserToDeactivate(null)} centered>
                <Modal.Header closeButton>
                  <Modal.Title>Deactivate Employee</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {userToDeactivate && getDeactivateMessage(userToDeactivate)}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setUserToDeactivate(null)}>
                    No
                  </Button>
                  <Button variant="primary" onClick={handleDeactivateConfirm}>
                    Yes
                  </Button>
                </Modal.Footer>
              </Modal>
            </>
          )}
        </Tab>
        )}

        {canSeeClassifications && (
          <Tab eventKey="classifications" title="Classifications & Rates">
            <FiltersCard
              title="Classification Filters"
              subtitle="Filter classifications by service area"
              showApplyButton={false}
              showClearButton={false}
            >
              <Row className="tw-items-end">
                <Col md={4}>
                  <Form.Group>
                    <Form.Label className="tw-mb-1 tw-flex tw-items-center tw-gap-1.5 tw-font-semibold tw-text-content-secondary">
                      <i className="bi bi-geo-alt-fill tw-text-primary" aria-hidden="true"></i>
                      Service Area
                    </Form.Label>
                    <Form.Select
                      value={selectedServiceAreaId}
                      onChange={(e) => {
                        setSelectedServiceAreaId(e.target.value ? parseInt(e.target.value) : '');
                        setClassificationPagination(prev => ({ ...prev, page: 1 }));
                      }}
                    >
                      <option value="">All Service Areas</option>
                      {serviceAreas.map(sa => (
                        <option key={sa.id} value={sa.id}>
                          {sa.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </FiltersCard>

            {/* Search field appears below filters when visible */}
            {isClassificationSearchVisible && (
              <Row className="tw-mb-3">
                <Col xs={12}>
                  <Card className="tw-shadow-sm tw-border-0">
                    <Card.Body>
                      <InputGroup>
                        <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                          <i className="bi bi-search"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          className="tw-border-line-focus"
                          placeholder="Search by classification code..."
                          value={classificationSearchInput}
                          onChange={(e) => handleClassificationSearchInputChange(e.target.value)}
                          onKeyDown={handleClassificationSearchKeyPress}
                          autoFocus
                          size="lg"
                          style={{ fontSize: '1rem' }}
                        />
                        <Button
                          variant="primary"
                          size="lg"
                          onClick={handleClassificationSearch}
                          title="Search"
                        >
                          <i className="bi bi-search tw-mr-2"></i>
                          Search
                        </Button>
                        {(classificationSearchInput || classificationSearchTerm) && (
                          <Button
                            variant="outline-secondary"
                            size="lg"
                            onClick={clearClassificationSearch}
                            title="Clear search"
                          >
                            <i className="bi bi-x-lg"></i>
                          </Button>
                        )}
                      </InputGroup>
                      {classificationSearchTerm && (
                        <div className="tw-mt-2">
                          <Badge bg="info" text="white">
                            <i className="bi bi-search tw-mr-1"></i>
                            Searching for: "{classificationSearchTerm}"
                          </Badge>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {classificationError && (
              <Alert variant="danger" className="tw-shadow-sm tw-border-0">
                <i className="bi bi-exclamation-triangle-fill tw-mr-2"></i>
                {classificationError}
              </Alert>
            )}

            {classificationLoading ? (
              <div className="tw-text-center tw-py-5">
                <Spinner className="tw-text-primary" style={{ width: '3rem', height: '3rem' }}>Loading...</Spinner>
                <p className="tw-text-content-muted tw-mt-3">Loading classifications...</p>
              </div>
            ) : (
              <>
                <Card className="tw-shadow-sm tw-border-0" style={{ backgroundColor: '#ffffff', maxWidth: '100%' }}>
                  <Card.Body className="tw-p-0">
                    <div style={{ overflowX: 'auto' }}>
                      <Table hover className="tw-mb-0">
                        <thead>
                          <tr>
                            <th>Code</th>
                            <th>Service Area</th>
                            <th>Rate</th>
                            <th>Roles</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {classifications.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="tw-text-center tw-py-4">
                                <i className="bi bi-tags tw-text-6xl tw-font-light tw-text-content-muted"></i>
                                <h5 className="tw-mt-3">No classifications found</h5>
                                <p className="tw-text-content-muted">
                                  {selectedServiceAreaId || classificationSearchTerm
                                    ? 'Try adjusting your filters or search terms.'
                                    : 'Create your first classification to get started.'}
                                </p>
                              </td>
                            </tr>
                          ) : (
                            classifications.map((classification) => (
                              <tr key={classification.id}>
                                <td>
                                  <Badge bg="secondary" text="white">
                                    {classification.code}
                                  </Badge>
                                </td>
                                <td>
                                  {classification.serviceArea?.name || 'Unknown'}
                                </td>
                                <td>
                                  <strong>${classification.rate.toFixed(2)}</strong>
                                </td>
                                <td>
                                  {classification.roleClassifications && classification.roleClassifications.length > 0 ? (
                                    <div className="tw-flex tw-flex-wrap tw-gap-1">
                                      {classification.roleClassifications.map((rc) => (
                                        <Badge key={rc.id} bg="info" text="dark">
                                          {rc.role.name}
                                        </Badge>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="tw-text-content-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  <div className="tw-flex tw-gap-2">
                                    {canEditClassification && (
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => handleEditClassification(classification)}
                                        title="Edit Classification"
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </Button>
                                    )}
                                    {canEditClassification && (
                                      <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDeleteClassification(classification.id)}
                                        title="Delete Classification"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>

                <div className="tw-mt-3">
                  <ServerPagination
                    currentPage={classificationPagination.page}
                    totalPages={classificationPagination.totalPages}
                    totalCount={classificationPagination.total}
                    pageSize={classificationPagination.limit}
                    onPageChange={handleClassificationPageChange}
                    onPageSizeChange={handleClassificationPageSizeChange}
                    loading={classificationLoading}
                  />
                </div>
              </>
            )}

            {/* Classification Modal */}
            <ClassificationModal
              show={showClassificationModal}
              onHide={() => {
                setShowClassificationModal(false);
                setSelectedClassification(null);
              }}
              classification={selectedClassification}
              serviceAreas={serviceAreas}
              onSave={handleSaveClassification}
            />
          </Tab>
        )}

        {canSeeRoles && (
          <Tab eventKey="roles" title="Roles & Permissions">
            {isRoleSearchVisible && (
              <Row className="tw-mb-3">
                <Col xs={12}>
                  <Card className="tw-shadow-sm tw-border-0">
                    <Card.Body>
                      <InputGroup>
                        <InputGroup.Text className="tw-bg-primary tw-text-white tw-border-primary">
                          <i className="bi bi-search"></i>
                        </InputGroup.Text>
                        <Form.Control
                          type="text"
                          className="tw-border-line-focus"
                          placeholder="Search by role name..."
                          value={roleSearchInput}
                          onChange={(e) => setRoleSearchInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleRoleSearch();
                            }
                          }}
                          autoFocus
                          size="lg"
                          style={{ fontSize: '1rem' }}
                        />
                        <Button
                          variant="primary"
                          size="lg"
                          onClick={handleRoleSearch}
                          title="Search"
                        >
                          <i className="bi bi-search tw-mr-2"></i>
                          Search
                        </Button>
                        {(roleSearchInput || roleSearchTerm) && (
                          <Button
                            variant="outline-secondary"
                            size="lg"
                            onClick={clearRoleSearch}
                            title="Clear search"
                          >
                            <i className="bi bi-x-lg"></i>
                          </Button>
                        )}
                      </InputGroup>
                      {roleSearchTerm && (
                        <div className="tw-mt-2">
                          <Badge bg="info" text="white">
                            <i className="bi bi-search tw-mr-1"></i>
                            Searching for: "{roleSearchTerm}"
                          </Badge>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            <RolesTable
              roles={roles}
              permissions={permissions}
              onEditRole={handleEditRole}
              onDeleteRole={handleDeleteRole}
              sortBy={roleSortBy}
              sortDir={roleSortDir}
              onSort={handleRoleSort}
            />
            
            {rolePagination.totalPages > 1 && (
              <div className="tw-mt-3">
                <ServerPagination
                  currentPage={rolePagination.page}
                  totalPages={rolePagination.totalPages}
                  totalCount={rolePagination.totalCount}
                  pageSize={rolePagination.limit}
                  onPageChange={handleRolePageChange}
                  onPageSizeChange={(size) => setRolePagination(prev => ({ ...prev, limit: size, page: 1 }))}
                  loading={false}
                />
              </div>
            )}

          </Tab>
        )}
      </Tabs>
      </div>

      {/* User Modal */}
      <UserModal 
        show={showUserModal} 
        onHide={() => {
          setShowUserModal(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSave={handleSaveUser}
      />

      {/* User History Modal */}
      {selectedUser && (
        <UserHistoryModal
          visible={showHistoryModal}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedUser(null);
          }}
          userId={selectedUser.id}
          userName={`${selectedUser.name} ${selectedUser.lastName}`}
        />
      )}

      {/* Role Modal */}
      {hasPermission('role:create') && (
        <RoleModal
          show={showRoleModal}
          onHide={() => setShowRoleModal(false)}
          role={selectedRole}
          permissions={permissions}
          onSave={handleSaveRole}
        />
      )}

      {/* Season Change Confirmation Modal */}
      <Modal show={showSeasonConfirm} onHide={cancelSeasonChange} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill tw-text-warning-text tw-mr-2"></i>
            Confirm Bulk Season Change
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            All employees within the current filter will be affected across <strong>all pages</strong>.
          </p>
          <p>
            <strong>{bulkUpdateCount} employee{bulkUpdateCount !== 1 ? 's' : ''}</strong> across all pages will be switched to <strong>{pendingSeason === 'summer' ? 'Summer' : 'Winter'}</strong>.
          </p>
          <p className="tw-text-content-muted tw-mb-0">
            <i className="bi bi-info-circle tw-mr-1"></i>
            This will update all matching employees, not just those visible on the current page. Are you sure you want to proceed?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelSeasonChange}>
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmSeasonChange}>
            Confirm Change
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UsersPage;
